import { AuthSession, storedAuthToken, authTokenFromJSON } from "../utils/authSession"
import { SupportContextLease } from "../utils/supportContextLease"
import { toastError, toastInfo, toastSuccess } from "../utils/myToasts"
import { Container } from "unstated"

import { navigateAuth as navigate } from "../utils/authNavigation"
import { get, noop } from "lodash"
import { parse } from "query-string"

import {
  getCrispVerification,
  getProfile,
  getUserIpAddress,
} from "../utils/CallAPIs"
import logrocket from "../utils/logrocket"
import debounce from "../utils/debounce"
import api from "../utils/api"
import { isCurrentRouteProtected } from "../utils/routes"
import {
  applyCrispVerifiedIdentity,
  disconnectCrispAccountContext,
  markCrispIdentityUnverified,
} from "../utils/crispIdentity"

const isBrowser = typeof window !== "undefined"

const genericError = "Oops! something bad happened. Please try again later."

export class AuthContainer extends Container {
  constructor(props) {
    super(props)

    this.state = {
      user: null,
      token: null,
      error: null,
    }

    this.authSession = new AuthSession(() =>
      isBrowser ? storedAuthToken(localStorage) : null
    )
    this.crispSync = null
    this.supportContext = new SupportContextLease({
      bind: (token, sessionId) => api.post("/account/support_context", { session_id: sessionId }, { token, timeout: 8000 }).then(response => response.data),
      readSession: () => window.$crisp && typeof window.$crisp.get === "function" ? window.$crisp.get("session:identifier") : null,
      onRejected: (token, error) => this.denyCrispAccountContext(token, this.authSession.capture(), error),
    })
    this.silentAuth = debounce(this.silentAuth)
    if (isBrowser) {
      window.addEventListener("storage", event => {
        if (event.key !== "user" && event.key !== null) return
        if (event.storageArea && event.storageArea !== localStorage) return
        if (event.key === "user" && authTokenFromJSON(event.oldValue) === authTokenFromJSON(event.newValue)) return
        if (event.key === null && storedAuthToken(localStorage) === this.state.token) return
        this.cancelCrispRetry()
    this.authSession.invalidate()
        this.supportContext.stop()
        this.setState({ user: null, token: null, error: null })
        markCrispIdentityUnverified({ resetSession: true })
        // Discard other stores' cached account data on an external session change.
        window.location.reload()
      })
    }
  }

  get isAuthenticated() {
    if (!isBrowser) {
      return false
    }

    return !!this.state.token
  }

  clearErrors = callback => {
    this.setState({ error: null }, callback)
  }

  login = async () => {
    this.cancelCrispRetry()
    this.authSession.invalidate()
    const session = this.authSession.capture()
    if (!await this.revokeSupportContext(session.token || this.state.token) || !this.authSession.current(session)) return
    markCrispIdentityUnverified({ resetSession: true })
    await this.setState(() => this.authSession.current(session) ? { user: null, token: null, error: null } : {})
    if (!this.authSession.current(session)) return
    let targetUrl = window.location.pathname
    const blackListed = ["/", "/login-error", "/createaccount", "/createaccount/"]

    if (blackListed.includes(targetUrl)) {
      targetUrl = localStorage.targetUrl ? localStorage.targetUrl : "/proxies"
    } else {
      targetUrl += window.location.search
    }

    localStorage.removeItem("user")
    localStorage.setItem("targetUrl", targetUrl)
    navigate(`/createaccount?form=select_method`)
  }

  loginUser = async ({ email, password, greToken, v2 }) => {
    this.cancelCrispRetry()
    this.authSession.invalidate()
    let session = this.authSession.capture()
    if (!session.token) markCrispIdentityUnverified()
    if (session.token) {
      if (!await this.revokeSupportContext(session.token) || !this.authSession.current(session)) return
      localStorage.removeItem("user")
      session = this.authSession.capture()
      await this.setState(() => this.authSession.current(session) ? { user: null, token: null } : {})
      if (!this.authSession.current(session)) return
      markCrispIdentityUnverified({ resetSession: true })
      session = this.authSession.capture()
    }
    try {
      this.setState({ error: null })
      const ipDetails = await getUserIpAddress("ipv4").catch(() => ({
        ip: null,
      }))
      const ipv4 = ipDetails?.ip ?? null
      const res = await api.post("/auth/signin", {
        email,
        password,
        ipv4,
        gre_token: greToken,
        v2,
      })
      const { token } = res.data
      return await this.getProfile(token, this.handleRedirect, session)
    } catch (e) {
      if (!this.authSession.current(session)) return
      const status = get(e, "response.status")

      if (status === 400) {
        return { status: 400 }
      }

      const error = get(e, "response.data.msg", genericError)
      await this.setState({ error })
      console.error(e)
    }
  }

  signup = async ({ email, password, greToken, v2 }) => {
    try {
      this.setState({ error: null })

      const ipDetails = await Promise.all([
        getUserIpAddress("ipv4").catch(() => ({ ip: null })),
        getUserIpAddress("ipv6").catch(() => ({ ip: null })),
      ])

      const ipv4 = ipDetails?.[0]?.ip ?? null
      const ipv6 = ipDetails?.[1]?.ip ?? null

      const res = await api.post("/auth/signup", {
        email,
        password,
        ipv4,
        ipv6,
        gre_token: greToken,
        v2,
      })
      const {
        data: { data },
      } = res
      return data
    } catch (e) {
      const status = get(e, "response.status")

      if (status === 400) {
        return { status: 400 }
      }

      const error = get(e, "response.data.msg", genericError)
      await this.setState({ error })
      console.error(e)
    }
  }

  acccountCreateSurvey = async ({
    email,
    sourceFromWhereUserHeardUs,
    serviceProvider,
    locationToOffer,
  }) => {
    try {
      this.setState({ error: null })
      const res = await api.post("/auth/account_creation_survey", {
        email,
        source_from_where_user_heard_us: sourceFromWhereUserHeardUs,
        service_provider: serviceProvider,
        location_to_offer: locationToOffer,
      })
      const { msg } = res.data
      return msg
    } catch (e) {
      const status = get(e, "response.status")

      if (status === 400) {
        return { status: 400 }
      }

      const error = get(e, "response.data.msg", genericError)
      await this.setState({ error })
      console.error(e)
    }
  }

  requestReset = async ({ email, greToken, v2 }) => {
    try {
      this.setState({ error: null })
      const res = await api.post("/auth/request_reset_password", {
        email,
        gre_token: greToken,
        v2,
      })
      const { msg } = res.data
      navigate(`/createaccount?form=reset_succeed`)
      return msg
    } catch (e) {
      const status = get(e, "response.status")

      if (status === 400) {
        return { status: 400 }
      }

      const error = get(e, "response.data.msg", genericError)
      await this.setState({ error })
      console.error(e)
    }
  }

  handleRedirect = () => {
    const targetUrl = localStorage.targetUrl || "/proxies"
    navigate(targetUrl)
    localStorage.removeItem("targetUrl")
  }

  cancelCrispRetry = () => {
    if (this.crispSync && this.crispSync.timer !== null) window.clearTimeout(this.crispSync.timer)
    if (this.crispSync) this.crispSync.timer = null
  }

  crispSyncFor = (token, session) => {
    if (!this.crispSync || this.crispSync.token !== token || this.crispSync.epoch !== session.epoch) {
      const denied = !!this.crispSync && this.crispSync.token === token && this.crispSync.denied
      this.cancelCrispRetry()
      this.crispSync = { token, epoch: session.epoch, denied, attempts: 0, nextAttempt: 0, pending: null, timer: null }
    }
    return this.crispSync
  }

  notifyCrispDenied = async (token, session, message) => {
    // Persist only a SHA-256 fingerprint for notice suppression, never a JWT.
    // This is a UI preference, never account-access authority.
    let key
    try {
      const bytes = await window.crypto.subtle.digest("SHA-256", new window.TextEncoder().encode(token))
      key = Array.from(new Uint8Array(bytes), value => value.toString(16).padStart(2, "0")).join("")
      if (window.sessionStorage.getItem("mp_crisp_denial_notice") === key) return
    } catch (e) {}
    if (!this.authSession.current(session) || this.state.token !== token) return
    try { if (key) window.sessionStorage.setItem("mp_crisp_denial_notice", key) } catch (e) {}
    toastInfo(message)
  }

  denyCrispAccountContext = (token, session = this.authSession.capture(), error) => {
    if (!this.authSession.current(session) || this.state.token !== token) return
    const sync = this.crispSyncFor(token, session)
    if (sync.denied) return
    sync.denied = true
    this.cancelCrispRetry()
    this.supportContext.stop()
    const code = get(error, "response.data.error")
    const needsSignin = ["support_signin_required", "support_authentication_failed"].includes(code)
    disconnectCrispAccountContext(needsSignin ? "sign_in_required" : "unavailable")
    const message = needsSignin
      ? "Account details need a new sign-in. Your operator chat is still available. Signing out will reset this chat."
      : "Account details are unavailable for this session. Your operator chat is still available."
    this.crispNoticeTask = this.notifyCrispDenied(token, session, message)
    return this.crispNoticeTask
  }

  syncCrispIdentity = (token, session = this.authSession.capture()) => {
    if (!this.authSession.current(session) || this.state.token !== token) return
    const sync = this.crispSyncFor(token, session)
    if (sync.denied || sync.attempts >= 4) return
    if (sync.pending) return sync.pending
    if (Date.now() < sync.nextAttempt) return
    this.cancelCrispRetry()
    sync.pending = (async () => {
      try {
        const verification = await getCrispVerification(token)
        if (!this.authSession.current(session) || this.state.token !== token || this.crispSync !== sync || sync.denied) return
        applyCrispVerifiedIdentity(verification)
        sync.attempts = 0
        sync.nextAttempt = Date.now() + 60000
      } catch (error) {
        if (!this.authSession.current(session) || this.state.token !== token || this.crispSync !== sync || sync.denied) return
        const status = get(error, "response.status")
        if ([400, 401, 403, 404, 422].includes(status)) {
          this.denyCrispAccountContext(token, session, error)
        } else {
          // Three scheduled retries (30s, 60s, 120s), then stop until a new
          // document/session. Current server leases still gate account outputs.
          const delay = 30000 * Math.pow(2, sync.attempts++)
          sync.nextAttempt = Date.now() + delay
          if (sync.attempts < 4) sync.timer = window.setTimeout(() => {
            sync.timer = null
            sync.nextAttempt = 0 // The scheduled delay elapsed; tolerate wall-clock adjustments.
            if (this.authSession.current(session) && this.state.token === token && this.crispSync === sync && !sync.denied) this.syncCrispIdentity(token, session)
          }, delay)
        }
      } finally {
        sync.pending = null
      }
    })()
    return sync.pending
  }

  getProfile = async (token = this.state.token, cb = noop, session = this.authSession.capture()) => {
    if (!this.authSession.current(session)) return
    const user = await getProfile(token)
    return this.applyProfile(user, token, cb, session)
  }

  applyProfile = async (user, token, cb, session) => {
    if (!this.authSession.current(session)) return
    await this.setState(() => this.authSession.current(session)
      ? { user, error: null, token }
      : {})
    if (!this.authSession.current(session)) return
    localStorage.setItem("user", JSON.stringify({ ...user, token }))
    const supportSession = this.authSession.capture()
    if (!this.crispSyncFor(token, supportSession).denied) {
      this.supportContext.start(token, () => this.authSession.current(supportSession) && this.state.token === token)
    }
    logrocket.identify(user)
    this.syncCrispIdentity(token, this.authSession.capture())
    cb(user)
    return user
  }

  // Background UI refreshes are best effort. Keep cached account state on a
  // failed request; sign-in and session checks handle failures explicitly.
  refreshProfile = async (token = this.state.token, cb = noop, session = this.authSession.capture()) => {
    if (!this.authSession.current(session)) return
    let user, failed = false
    try {
      user = await getProfile(token)
    } catch (error) {
      failed = true
    }
    if (!this.authSession.current(session)) return
    if (failed) {
      cb(undefined)
      return undefined
    }
    return this.applyProfile(user, token, cb, session)
  }

  // A completed reset/confirmation cannot be retried with its consumed link.
  // Fetch before committing a session, and report success even during an outage.
  completeAccountAction = async (token, session, cb = this.handleRedirect, message = "Account updated.") => {
    if (!this.authSession.current(session)) return
    let user
    try {
      user = await getProfile(token)
    } catch (error) {
      if (!this.authSession.current(session)) return
      this.supportContext.stop()
      localStorage.removeItem("user")
      this.cancelCrispRetry()
    this.authSession.invalidate()
      const cleared = this.authSession.capture()
      await this.setState(() => this.authSession.current(cleared) ? { user: null, token: null, error: null } : {})
      if (!this.authSession.current(cleared)) return
      markCrispIdentityUnverified({ resetSession: true })
      toastSuccess(`${message} Please sign in.`)
      navigate("/createaccount?form=login")
      return true
    }
    try {
      return await this.applyProfile(user, token, cb, session)
    } catch (error) {
      // The action and profile fetch succeeded. An automatic sign-in failure
      // must not invite another use of the consumed reset/confirmation link.
      if (this.authSession.epoch !== session.epoch) return
      const storedToken = storedAuthToken(localStorage)
      if (storedToken && storedToken !== token) return
      console.error("Account updated, but automatic sign-in could not finish.")
      toastSuccess(`${message} Please sign in.`)
      navigate("/createaccount?form=login")
      return true
    }
  }

  silentAuth = async (callback = noop) => {
    let session = this.authSession.capture()
    let { user, token } = this.state
    const forceLogin = isCurrentRouteProtected()

    if (!token && localStorage.user && localStorage.user !== "undefined") {
      try {
        const { token: savedTokens, ...rest } = JSON.parse(localStorage.user)
        user = rest
        token = savedTokens

        await this.setState(() => this.authSession.current(session) ? { user, token } : {})
        if (!this.authSession.current(session)) return
      } catch (e) {
        console.error(e)
      }
    }

    if (token) {
      let profile, error
      try {
        profile = await this.getProfile(token, noop, session)
      } catch (e) {
        console.error("Unable to refresh account profile.")
        error = e
      }
      if (!this.authSession.current(session)) return
      if (!profile || error) {
        if (![401, 403].includes(get(error, "response.status"))) {
          // A temporary profile outage is not a sign-out on every device.
          return callback()
        }
        if (!await this.revokeSupportContext(token)) return callback()
        if (!this.authSession.current(session)) return
        localStorage.removeItem("user")
        token = null
        session = this.authSession.capture()
        await this.setState(() => this.authSession.current(session) ? { user: null, token: null } : {})
        if (!this.authSession.current(session)) return
        this.supportContext.stop()
        markCrispIdentityUnverified({ resetSession: true })
      }
    }

    if (!this.authSession.current(session)) return
    if (!token && !forceLogin) {
      markCrispIdentityUnverified()
    }

    if (!token && forceLogin) {
      return this.login()
    }

    return callback()
  }

  confirmEmail = async () => {
    const { token } = parse(window.location.search)

    if (!token) {
      return navigate("/")
    }

    this.cancelCrispRetry()
    this.authSession.invalidate()
    let session = this.authSession.capture()
    if (!session.token) markCrispIdentityUnverified()
    if (session.token) {
      if (!await this.revokeSupportContext(session.token) || !this.authSession.current(session)) return
      localStorage.removeItem("user")
      session = this.authSession.capture()
      await this.setState(() => this.authSession.current(session) ? { user: null, token: null } : {})
      if (!this.authSession.current(session)) return
      markCrispIdentityUnverified({ resetSession: true })
      session = this.authSession.capture()
    }
    let res
    try {
      this.setState({ error: null })
      res = await api.post("/auth/confirm_account", {
        token,
      })
    } catch (e) {
      if (!this.authSession.current(session)) return
      console.error("Unable to confirm account.")
      const error = get(e, "response.data.msg", genericError)
      await this.setState({ error })
      return navigate("/createaccount")
    }
    if (!this.authSession.current(session)) return
    localStorage.setItem("targetUrl", "/proxies")
    return this.completeAccountAction(res.data.token, session, this.handleRedirect, "Email confirmed.")
  }

  resetPassword = async ({ password }) => {
    if (!isBrowser) {
      return
    }

    const { token } = parse(window.location.search)

    if (!token) {
      return navigate("/")
    }

    this.cancelCrispRetry()
    this.authSession.invalidate()
    let session = this.authSession.capture()
    if (!session.token) markCrispIdentityUnverified()
    if (session.token) {
      if (!await this.revokeSupportContext(session.token) || !this.authSession.current(session)) return
      localStorage.removeItem("user")
      session = this.authSession.capture()
      await this.setState(() => this.authSession.current(session) ? { user: null, token: null } : {})
      if (!this.authSession.current(session)) return
      markCrispIdentityUnverified({ resetSession: true })
      session = this.authSession.capture()
    }
    let res
    try {
      this.setState({ error: null })
      res = await api.post("/auth/reset_password", {
        token,
        password,
      })
    } catch (e) {
      if (!this.authSession.current(session)) return
      const error = get(e, "response.data.msg", genericError)
      await this.setState({ error })
      console.error("Unable to reset password.")
      return
    }
    return this.completeAccountAction(res.data.token, session, this.handleRedirect, "Password changed.")
  }

  revokeSupportContext = async token => {
    this.supportContext.stop()
    if (!token) return true
    try {
      await api.post("/account/support_context/revoke", {}, { token, timeout: 8000 })
      return true
    } catch (error) {
      if ([401, 403].includes(get(error, "response.status"))) return true
      // Failed logout leaves the app session current. Resume its lease without
      // allowing this old response to restart work for a different account.
      const session = this.authSession.capture()
      if (session.token === token && this.state.token === token && !this.crispSyncFor(token, session).denied) {
        this.supportContext.start(token, () => this.authSession.current(session) && this.state.token === token)
      }
      toastError("Sign out could not be confirmed. Please try again.")
      return false
    }
  }

  logout = async (cb = noop) => {
    this.cancelCrispRetry()
    this.authSession.invalidate()
    const session = this.authSession.capture()
    if (!await this.revokeSupportContext(session.token || this.state.token) || !this.authSession.current(session)) return false
    localStorage.removeItem("user")
    const cleared = this.authSession.capture()
    markCrispIdentityUnverified({ resetSession: true, reloadPage: true })
    await this.setState(() => this.authSession.current(cleared) ? { user: null, token: null } : {})
    if (!this.authSession.current(cleared)) return false
    cb()
    return true
  }

  updateProfile = async (attr, value) => {
    const session = this.authSession.capture()
    try {
      const res = await api.put("/account/update", {
        [attr]: value,
      })

      if (!this.authSession.current(session)) return
      let { user, token } = this.state
      user = { ...user, ...res.data.data.attributes }
      await this.setState(() => this.authSession.current(session) ? { user } : {})
      if (!this.authSession.current(session)) return
      localStorage.setItem("user", JSON.stringify({ token, ...user }))
    } catch (e) {
      if (!this.authSession.current(session)) return
      const error = get(e, "response.data.msg", genericError)
      await this.setState({ error })
      console.error(e)
    }
  }
}

export const authStore = new AuthContainer()

export default authStore
